import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/user';
import { showToast } from '../components/Toast';

export function Account() {
  usePageTitle('Account');
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { credits } = useUserStore();

  const handleDeleteAccount = async () => {
    if (!confirm('Are you sure? This will permanently delete your account and all data.')) return;
    // In production, call DELETE /user/account endpoint
    await signOut();
    navigate('/');
  };

  return (
    <div className="pt-24 pb-16 px-5 sm:px-8 max-w-2xl mx-auto animate-fade-in">
      <h1 className="font-sans text-3xl text-ink mb-10" style={{ fontWeight: 600 }}>
        Account
      </h1>

      <div className="space-y-6">
        {/* Email */}
        <div className="card">
          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
            Email
          </div>
          <div className="text-sm font-sans text-ink">{user?.email || '—'}</div>
        </div>

        {/* Credits */}
        <div className="card">
          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
            Credit Balance
          </div>
          <div className="flex items-center justify-between">
            <div className="text-2xl font-sans text-ink" style={{ fontWeight: 600 }}>
              {credits}
            </div>
            <button onClick={() => navigate('/purchase')} className="btn-rose text-xs">
              Buy more
            </button>
          </div>
        </div>

        {/* Member since */}
        <div className="card">
          <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
            Member Since
          </div>
          <div className="text-sm font-sans text-ink">
            {user?.created_at
              ? new Date(user.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })
              : '—'}
          </div>
        </div>

        {/* Referral */}
        <div className="card-rose">
          <div className="text-[11px] font-sans text-rose tracking-wider uppercase mb-2 font-medium">
            Share PhotoFeeler
          </div>
          <p className="text-sm text-ink-muted mb-4" style={{ fontWeight: 300 }}>
            Know someone working on their dating or LinkedIn profile? Share PhotoFeeler and they&apos;ll get 3 free credits to try it.
          </p>
          <div className="flex gap-2">
            <input
              readOnly
              value={`${window.location.origin}/?ref=${user?.id?.slice(0, 8) || 'friend'}`}
              className="input text-xs flex-1"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              onClick={() => {
                const link = `${window.location.origin}/?ref=${user?.id?.slice(0, 8) || 'friend'}`;
                navigator.clipboard.writeText(link)
                  .then(() => showToast('Referral link copied!', 'success'))
                  .catch(() => showToast('Couldn\u0027t copy to clipboard. Try selecting the link manually.', 'error'));
              }}
              className="btn-rose text-xs shrink-0"
            >
              Copy link
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="pt-6 border-t border-surface-border space-y-3">
          <button
            onClick={() => {
              signOut();
              navigate('/');
            }}
            className="btn-secondary w-full text-xs"
          >
            Log out
          </button>
          <button
            onClick={handleDeleteAccount}
            className="w-full text-xs font-sans text-ink-faint hover:text-rose transition-colors py-2"
          >
            Delete account
          </button>
          <p className="text-[11px] text-ink-faint text-center" style={{ fontWeight: 300 }}>
            Deleting your account will remove all your data, photos, and credits permanently.
          </p>
        </div>
      </div>
    </div>
  );
}
